import { Directionality } from "@angular/cdk/bidi";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  Input,
  Optional,
  QueryList,
  Renderer2,
  TemplateRef,
} from "@angular/core";
import { NzCardComponent } from "ng-zorro-antd/card";
import { NzConfigService, WithConfig } from "ng-zorro-antd/core/config";
import { NgStyleInterface } from "ng-zorro-antd/core/types";
import { InputBoolean } from "ng-zorro-antd/core/util";
import { UiCardTabComponent } from "@webapp/ui/card/components/card-tab/card-tab.component";
import { UiCardGridDirective } from "@webapp/ui/card/directives/card-grid.directive";
import { UiSizeDSType } from "@webapp/ui/core/models/ui-size.models";

@Component({
  selector: "ui-card",
  exportAs: "uiCard",
  templateUrl: "card.component.html",
  styleUrls: ["./card.component.less"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: "ant-card",
    "[class.ant-card-loading]": "nzLoading",
    "[class.ant-card-bordered]": "nzBorderless === false && nzBordered",
    "[class.ant-card-hoverable]": "nzHoverable",
    "[class.ant-card-small]": 'nzSize === "small"',
    "[class.ant-card-contain-grid]": "listOfNzCardGridDirective && listOfNzCardGridDirective.length",
    "[class.ant-card-type-inner]": 'nzType === "inner"',
    "[class.ant-card-contain-tabs]": "!!listOfNzCardTabComponent",
    "[class.ant-card-rtl]": `dir === 'rtl'`,
    "[class.rounded]": "uiRounded",
  },
})
export class UiCardComponent extends NzCardComponent implements AfterViewInit {
  @Input("uiBordered") @WithConfig() @InputBoolean() public nzBordered = true;
  @Input("uiBorderless") @WithConfig() @InputBoolean() public nzBorderless = false;
  @Input("uiLoading") @InputBoolean() public nzLoading = false;
  @Input("uiHoverable") @WithConfig() @InputBoolean() public nzHoverable = false;
  @Input("uiBodyStyle") public nzBodyStyle: NgStyleInterface | null = null;
  @Input("uiCover") public nzCover?: TemplateRef<void>;
  @Input("uiActions") public nzActions: Array<TemplateRef<void>> = [];
  @Input("uiType") public nzType: string | "inner" | null = null;
  @Input("uiSize") @WithConfig() public nzSize: UiSizeDSType = "default";
  @Input("uiTitle") public nzTitle?: string | TemplateRef<void>;
  @Input("uiExtra") public nzExtra?: string | TemplateRef<void>;
  @Input() @InputBoolean() public uiHeadBorderless = false;
  @Input() @InputBoolean() public uiRounded = false;
  @Input() public uiDescription?: string | TemplateRef<void>;
  @ContentChild(UiCardTabComponent, { static: false }) public listOfNzCardTabComponent?: UiCardTabComponent;
  @ContentChildren(UiCardGridDirective) public listOfNzCardGridDirective!: QueryList<UiCardGridDirective>;

  constructor(
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    cdr: ChangeDetectorRef,
    uiConfigService: NzConfigService,
    @Optional() directionality: Directionality
  ) {
    super(uiConfigService, cdr, directionality);
  }

  public ngAfterViewInit(): void {
    const element = this.elementRef.nativeElement.querySelector<HTMLElement>(".ant-card-body");

    const showHideScrollShadows = (): void => {
      const SHADOW_HEIGHT = 8;
      const hasScrolledTop = element.scrollTop < SHADOW_HEIGHT;
      if (hasScrolledTop) {
        this.renderer.removeClass(element, "scrollable-shadow-top");
      } else {
        this.renderer.addClass(element, "scrollable-shadow-top");
      }
    };
    element.addEventListener("scroll", showHideScrollShadows);
    new ResizeObserver(showHideScrollShadows).observe(element);
  }
}
